import { computed, inject, Injectable, Signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { CalendarIntegrationStore, FocusStore, HabitStore, JournalStore, ListStore, MoneyTrackerStore, NoteStore, TodoStore, ViewSettingsStore } from '@app/_stores';
import { SettingsService } from '../settings.service';
import { MatDialog } from '@angular/material/dialog';
import { ModalService } from '../modal.service';
// NOTE: Form components are loaded dynamically to break circular dependencies
import { List } from '@app/_interfaces/list.interface';
import { Todo } from '@app/_interfaces/todo.interface';
import { Note } from '@app/_interfaces/note.interface';
import { MoneyTrackerSetup, MoneyTrackerAction } from '@app/_interfaces/money-tracker.interface';
import { JournalSetup } from '@app/_interfaces/journal.interface';
import { AlertService } from '../alert.service';
import { CalendarIntegrationType, DayCode, EntityName, HabitType } from '@app/_types/generic.type';
import { HabitSetup } from '@app/_interfaces/habit.interface';
import { CalendarIntegration } from '@app/_interfaces/calendar-integration.interface';
import { CacheService } from '../cache.service';
import { User } from '@app/_interfaces/user.interface';
import { FocusSetup } from '@app/_interfaces/focus-tracker.interface';
import { getLocalDateString as getDateString, parseDateString } from '@app/_services/date';
import { log } from '@app/_services/logger';
import { Entry } from '@app/_models/entry.model';
import { EntryService } from '@app/_services/entry.service';

@Injectable({
    providedIn: 'root',
})

export class FeatureService {

    readonly listStore = inject(ListStore);
    readonly noteStore = inject(NoteStore);
    readonly todoStore = inject(TodoStore);
    readonly habitStore = inject(HabitStore);
    readonly journalStore = inject(JournalStore);
    readonly moneyTrackerStore = inject(MoneyTrackerStore);
    readonly calendarStore = inject(CalendarIntegrationStore);
    readonly focusStore = inject(FocusStore);
    readonly viewSettingsStore = inject(ViewSettingsStore);
    readonly entryService = inject(EntryService);

    earliestDateString: Signal<string> = computed(() => {
        const todoDateString = this.todoStore.firstDateString();
        const habitDateString = this.habitStore.firstDateString();
        const journalDateString = this.journalStore.firstDateString();
        const moneyTrackerDateString = this.moneyTrackerStore.firstDateString();
        const calendarDateString = this.calendarStore.firstDateString();

        const dateStrings = [
            todoDateString,
            habitDateString,
            journalDateString,
            moneyTrackerDateString,
            calendarDateString
        ].filter(Boolean) as string[];

        // Convert YYYY-MM-DD → Date (app uses ISO date format)
        const toDate = (ds: string): Date => {
            const [year, month, day] = ds.split('-').map(Number);
            return new Date(year, month - 1, day);
        };

        // Find the minimum (oldest) date
        const minDate = dateStrings.reduce((min, current) => {
            return toDate(current) < toDate(min) ? current : min;
        }, dateStrings[0]);

        return minDate;
    });

    user: WritableSignal<User> = this.ss.user;
    subscriptionExpDate: Signal<Date | null> = computed(() => {
        return this.user()?.subscriptionInfo?.subscriptionExpDate || null;
    });

    constructor(
        private ss: SettingsService,
        private dialog: MatDialog,
        private modalService: ModalService,
        private alertService: AlertService,
        private cc: CacheService,
        private router: Router,
    ) {
    }

    /**
     * Check if a specific date has any visible entities.
     * Delegates to EntryService (same resolvers as the UI).
     */
    hasEntitiesOnDate(dateString: string): boolean {
        return this.entryService.getEntriesForDate(dateString).length > 0;
    }

    /**
     * ✅ Get hasData flags for multiple dates efficiently
     * Returns a map of dateString -> hasData
     */
    getHasDataForDates(dateStrings: string[]): Record<string, boolean> {
        const result: Record<string, boolean> = {};
        dateStrings.forEach(dateString => {
            result[dateString] = this.hasEntitiesOnDate(dateString);
        });
        return result;
    }

    async openTodoForm(todo: Todo | null, dateString: string | null, isAlert: boolean = false) {
        const { TodoFormComponent } = await import('@app/components/features/todos/todo-form/todo-form.component');
        const todoForm = this.modalService.openLevel1(TodoFormComponent,
            {
                mode: todo && todo.id ? 'edit' : 'new',
                value: todo ? todo : null,
                dateString: dateString
            },
            { disableClose: true }
        );

        return this.modalService.getClosedObservable(todoForm).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('todoCreated_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const todo = this.todoStore.idToTodo()[result.id];
                this.openTodoForm(todo, result.dateString);
            }
        });
    }

    async openListForm(list?: List | null, isAlert: boolean = false) {
        const { ListFormComponent } = await import('@app/components/features/lists/list-form/list-form.component');
        const listForm = this.modalService.openLevel3(ListFormComponent, {
            mode: list && list.id ? 'edit' : 'new',
            value: list ? list : null
        }, {
            disableClose: true
        });
        return this.modalService.getClosedObservable(listForm).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('listCreated_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const list = this.listStore.idToList()[result.id];
                const { ListItemsComponent } = await import('@app/components/features/lists/list-items/list-items.component');
                this.modalService.openLevel1(ListItemsComponent,
                    {
                        value: list
                    },
                    { autoFocus: false }
                );
            }
        });
    };

    async openNoteForm(note?: Note | null, isAlert: boolean = false) {
        const { NoteFormComponent } = await import('@app/components/features/notes/note-form/note-form.component');
        const noteForm = this.modalService.openLevel1(NoteFormComponent,
            {
                mode: note && note.id ? 'edit' : 'new',
                value: note ? note : null,
                isAlert: isAlert
            },
            { disableClose: true }
        );
        return this.modalService.getClosedObservable(noteForm).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('noteCreated_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const note = this.noteStore.idToNote()[result.id];
                this.openNoteForm(note);
            }
        });
    };

    async openHabitTypeSelect() {
        const { HabitTypeSelectComponent } = await import('@app/components/features/habits/habit-type-select/habit-type-select.component');
        const habitTypeSelect = this.modalService.openModalLevel4(HabitTypeSelectComponent);
        return this.modalService.getClosedObservable(habitTypeSelect).subscribe(result => {
            if (result) {
                this.openHabitSetupForm(result, null, true);
            }
        });
    }

    async openHabitSetupForm(habitType: HabitType, setup?: HabitSetup | null, isAlert: boolean = false) {
        const { HabitSetupFormComponent } = await import('@app/components/features/habits/habit-setup-form/habit-setup-form.component');

        const data = {
            mode: setup && setup.id ? 'edit' : 'new',
            value: setup ? setup : null,
            habitType: habitType,
        };
        let setupHabitDialog;
        if (this.cc.isNarrowScreen()) {
            setupHabitDialog = this.modalService.openMainContent(
                HabitSetupFormComponent,
                data,
                { disableClose: true, autoFocus: false }
            );
        } else {
            setupHabitDialog = this.modalService.openLevel1(
                HabitSetupFormComponent,
                data,
                { disableClose: true, autoFocus: false }
            );
        }
        return this.modalService.getClosedObservable(setupHabitDialog).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('habitSetupCreated_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const setup = this.habitStore.idToSetup()[result.id];
                this.openHabitSetupForm(setup.habitType, setup);
            }
        });
    }

    async openJournalSetupForm(setup?: JournalSetup | null, isAlert: boolean = false) {
        const { JournalSetupFormComponent } = await import('@app/components/features/journals/journal-setup-form/journal-setup-form.component');
        const data = {
            mode: setup && setup.id ? 'edit' : 'new',
            value: setup ? setup : null,
        }

        let setupDialog;
        if (this.cc.isNarrowScreen()) {
            setupDialog = this.modalService.openMainContent(
                JournalSetupFormComponent,
                data,
                { disableClose: true, autoFocus: false }
            );
        } else {
            setupDialog = this.modalService.openLevel1(
                JournalSetupFormComponent,
                data,
                { disableClose: true, autoFocus: false }
            );
        }

        return this.modalService.getClosedObservable(setupDialog).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('journalSetupCreated_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const setup = this.journalStore.idToSetup()[result.id];
                this.openJournalSetupForm(setup);
            }
        });
    }

    async openMoneyTrackerSetupForm(setup?: MoneyTrackerSetup | null, isAlert: boolean = false) {
        const { MoneyTrackerSetupFormComponent } = await import('@app/components/features/money-tracker/money-tracker-setup-form/money-tracker-setup-form.component');
        const moneyData = {
            mode: setup && setup.id ? 'edit' : 'new',
            value: setup ? setup : null
        }
        let setupDialog;
        if (this.cc.isNarrowScreen()) {
            setupDialog = this.modalService.openMainContent(
                MoneyTrackerSetupFormComponent,
                moneyData,
                { disableClose: true, autoFocus: false }
            );
        } else {
            setupDialog = this.modalService.openLevel1(
                MoneyTrackerSetupFormComponent,
                moneyData,
                { disableClose: true, autoFocus: false }
            );
        }
        return this.modalService.getClosedObservable(setupDialog).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('moneyTrackerCreated_setupContent', 'common_view', 'common_dismiss');
                if (!isView) return;
                const setup = this.moneyTrackerStore.idToSetup()[result.id];
                this.openMoneyTrackerSetupForm(setup);
            }
        });
    };

    async openFocusSetupForm(setup?: FocusSetup | null, isAlert: boolean = false) {
        const { FocusSetupFormComponent } = await import('@app/components/features/focus-tracker/focus-setup-form/focus-setup-form.component');
        const data = {
            mode: setup && setup.id ? 'edit' : 'new',
            value: setup ? setup : null,
        }

        let setupDialog;
        if (this.cc.isNarrowScreen()) {
            setupDialog = this.modalService.openMainContent(
                FocusSetupFormComponent,
                data,
                { disableClose: true, autoFocus: false }
            );
        } else {
            setupDialog = this.modalService.openLevel1(
                FocusSetupFormComponent,
                data,
                { disableClose: true, autoFocus: false }
            );
        }

        this.modalService.getClosedObservable(setupDialog).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast('common_saved', 'common_view', 'common_dismiss');
                if (!isView) return;
                const setup = this.focusStore.setupsMap()[result.id];
                if (setup) {
                    this.openFocusSetupForm(setup);
                }
            }
        });
    }

    /**
     * Opens focus setup selection dialog (for FAB).
     * Shows unscheduled focus setups. User picks one → navigates to game screen.
     */
    async openFocusSetupSelect() {
        const { FocusSetupSelectComponent } = await import('@app/components/features/focus-tracker/focus-setup-select/focus-setup-select.component');
        const selectDialog = this.modalService.openModalLevel4(FocusSetupSelectComponent);
        return this.modalService.getClosedObservable(selectDialog).subscribe(result => {
            if (result) {
                this.router.navigate(['/focus-tracker', 1], { queryParams: { setupId: result } });
            }
        });
    }

    /**
     * Opens habit setup selection dialog (for FAB).
     * Shows unscheduled habit setups. User picks one → opens habit action form for today.
     */
    async openHabitSetupSelect() {
        const { HabitSetupSelectComponent } = await import('@app/components/features/habits/habit-setup-select/habit-setup-select.component');
        const selectDialog = this.modalService.openModalLevel4(HabitSetupSelectComponent);
        return this.modalService.getClosedObservable(selectDialog).subscribe(result => {
            if (result) {
                const dateString = getDateString();
                const habit = this.habitStore.habitMap().setups[result]?.[dateString] || null;
                this.openHabitActionForm(result, dateString, habit);
            }
        });
    }

    /**
     * Opens the habit action form for a specific setup and date.
     */
    private async openHabitActionForm(setupId: string, dateString: string, habit: any) {
        const { HabitFormComponent } = await import('@app/components/features/habits/habit-form/habit-form.component');
        this.modalService.openLevel1(
            HabitFormComponent,
            {
                mode: habit ? 'edit' : 'new',
                value: habit,
                setupId: setupId,
                dateString: dateString
            },
            { disableClose: true, autoFocus: false }
        );
    }

    /**
     * Opens journal setup selection dialog (for FAB).
     * Shows unscheduled journal setups. User picks one → opens journal action form for today.
     */
    async openJournalSetupSelect() {
        const { JournalSetupSelectComponent } = await import('@app/components/features/journals/journal-setup-select/journal-setup-select.component');
        const selectDialog = this.modalService.openModalLevel4(JournalSetupSelectComponent);
        return this.modalService.getClosedObservable(selectDialog).subscribe(result => {
            if (result) {
                const dateString = getDateString();
                const journal = this.journalStore.journalMap().setups[result]?.[dateString] || null;
                this.openJournalActionForm(result, dateString, journal);
            }
        });
    }

    /**
     * Opens the journal action form for a specific setup and date.
     */
    private async openJournalActionForm(setupId: string, dateString: string, journal: any) {
        const { JournalFormComponent } = await import('@app/components/features/journals/journal-form/journal-form.component');
        this.modalService.openLevel1(
            JournalFormComponent,
            {
                mode: journal ? 'edit' : 'new',
                value: journal,
                setupId: setupId,
                dateString: dateString
            },
            { disableClose: true, autoFocus: false }
        );
    }

    /**
     * Opens money tracker setup selection dialog (for FAB).
     * Shows editable setups. User picks one -> opens transaction form for that setup.
     */
    async openMoneyTrackerSetupSelect() {
        const { MoneyTrackerSetupSelectComponent } = await import('@app/components/features/money-tracker/money-tracker-setup-select/money-tracker-setup-select.component');
        const selectDialog = this.modalService.openModalLevel4(MoneyTrackerSetupSelectComponent);
        return this.modalService.getClosedObservable(selectDialog).subscribe(result => {
            if (result) {
                // Open a new transaction form pre-filled with the selected setup
                const setup = this.moneyTrackerStore.idToSetup()[result];
                if (setup) {
                    const transactionType = setup.defaultTransactionType || 'expense';
                    this.openMoneyTransactionForm(transactionType, null, true, setup.id);
                }
            }
        });
    }

    async openMoneyTransactionForm(transactionType: 'income' | 'expense', transaction?: MoneyTrackerAction | null, isAlert: boolean = false, setupId?: string) {
        const { MoneyTransactionFormComponent } = await import('@app/components/features/money-tracker/money-transaction-form/money-transaction-form.component');
        const transactionForm = this.modalService.openLevel1(
            MoneyTransactionFormComponent,
            {
                mode: transaction && transaction.id ? 'edit' : 'new',
                value: transaction ? transaction : null,
                transactionType: transactionType,
                setupId: setupId ?? null,
            },
            { disableClose: true, autoFocus: false }
        );
        return this.modalService.getClosedObservable(transactionForm).subscribe(async result => {
            if (result && result.id && isAlert) {
                const isView = await this.alertService.toast(transactionType === 'income' ? 'moneyTrackerCreated_incomeContent' : 'moneyTrackerCreated_expenseContent', 'common_view', 'common_dismiss');
                if (!isView) return;
                const transaction = this.moneyTrackerStore.idToTransaction()[result.id];
                this.openMoneyTransactionForm(transaction.transactionType, transaction);
            }
        });
    };

    async openCalendarTypeSelect() {
        const { CalendarTypeSelectComponent } = await import('@app/components/features/calendar-integration/calendar-type-select/calendar-type-select.component');
        const calendarTypeSelect = this.modalService.openModalLevel4(CalendarTypeSelectComponent);
        return this.modalService.getClosedObservable(calendarTypeSelect).subscribe(result => {
            if (result) {
                this.openCalendarConnectForm(result, true);
            }
        });
    };

    async openCalendarConnectForm(type: CalendarIntegrationType, isAlert: boolean = false) {
        const { CalendarConnectComponent } = await import('@app/components/features/calendar-integration/calendar-connect/calendar-connect.component');
        const calendarConnectDialog = this.dialog.open(CalendarConnectComponent, {
            width: '100%',
            maxWidth: '500px',
            height: 'auto',
            disableClose: true,
            data: {
                type: type,
                isAlert: isAlert
            },
        });
        return calendarConnectDialog.afterClosed().subscribe(async result => {
            if (result && isAlert) {
                const isView = await this.alertService.toast('connectCalendarSuccessfull_content', 'common_view', 'common_dismiss');
                if (!isView) return;
                const calendarAccountData = this.calendarStore.idToAccount()[result.id];
                this.openCalendarAccountForm('edit', calendarAccountData);
            }
        });
    };

    async openCalendarAccountForm(mode: 'new' | 'edit', calendarAccountData: CalendarIntegration, isAlert: boolean = false) {
        const { CalendarAccountFormComponent } = await import('@app/components/features/calendar-integration/calendar-account-form/calendar-account-form.component');
        if (this.cc.isNarrowScreen()) {
            this.modalService.openMainContent(CalendarAccountFormComponent, { mode: mode, value: calendarAccountData }, { disableClose: true });
        } else {
            this.modalService.openModalLevel1(CalendarAccountFormComponent, { mode: mode, value: calendarAccountData }, { disableClose: true });
        }

    };

    /**
     * Get all past entries for hashtag grouping.
     * Delegates to EntryService for RRULE expansion and visibility filtering,
     * then applies pagination-level filters (query, features, tags, etc.).
     * Limited to past 2 years for performance.
     */
    getAllPastEntries(
        filterQuery: string = '',
        filterFeatures: EntityName[] = [],
        filterTags: string[] = [],
        filterStartDate: Date | null = null,
        filterEndDate: Date | null = null,
        showInvalidEntries: boolean = false
    ): Entry[] {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        const twoYearsAgo = new Date(today);
        twoYearsAgo.setFullYear(twoYearsAgo.getFullYear() - 2);

        const toDateString = getDateString(yesterday);
        const fromDateString = getDateString(twoYearsAgo);

        // EntryService handles visibility filtering and RRULE expansion
        const entriesByDate = this.entryService.getEntriesForRange(fromDateString, toDateString);

        const result: Entry[] = [];
        const queryLower = filterQuery.toLowerCase();

        for (const [, entries] of Object.entries(entriesByDate)) {
            for (const entry of entries) {
                // Drifted entry filter
                if (!showInvalidEntries && entry.isDrifted) continue;

                // Query filter
                if (filterQuery && !entry.sortTitle.toLowerCase().includes(queryLower)) continue;

                // Feature filter
                if (filterFeatures.length > 0 && !filterFeatures.includes(entry.entityType)) continue;

                // Tag filter
                if (filterTags.length > 0 && !filterTags.some(tag => entry.tagIds.includes(tag))) continue;

                // Date range filter
                if (filterStartDate || filterEndDate) {
                    const entryDate = new Date(entry.date + 'T00:00:00');
                    if (filterStartDate && entryDate < filterStartDate) continue;
                    if (filterEndDate) {
                        const endOfDay = new Date(entry.date + 'T23:59:59.999');
                        if (endOfDay > filterEndDate) continue;
                    }
                }

                result.push(entry);
            }
        }

        return result;
    }

    getAllDatesOfEntity(ruleRecord: Record<string, string>, startDate: string, endDate: string | null, fromDateString: string, toDateString: string, showInvalidEntries: boolean = false): string[] {
        if (!startDate) return [];
        log.debug('getAllDatesOfEntity', 'Function called', { ruleRecord, startDate, endDate, fromDateString, toDateString });
        const isOccurrenceBasedRule = ruleRecord['COUNT'] ? true : false;
        if (isOccurrenceBasedRule) {
            const dateStrings: string[] = [];
            const count = Number(ruleRecord['COUNT'] || 0);
            if (count <= 0) return [];

            const currentDate = parseDateString(startDate) ?? new Date();

            while (dateStrings.length < count) {
                log.debug('getAllDatesOfEntity', 'Processing date', { isEligible: this.isElligible(currentDate, ruleRecord), count, currentLength: dateStrings.length });
                if (this.isElligible(currentDate, ruleRecord)) {
                    dateStrings.push(getDateString(currentDate));
                }
                currentDate.setDate(currentDate.getDate() + 1); // increment by 1 day
            }
            return dateStrings.filter(date => date >= fromDateString && date <= toDateString);
        } else {
            const dateStrings: string[] = [];
            const allDates = this.getDateRange(fromDateString, toDateString);
            allDates.forEach(date => {
                if ((this.isElligible(parseDateString(date) ?? new Date(), ruleRecord) || showInvalidEntries) && date >= startDate && (endDate ? date <= endDate : true)) {
                    dateStrings.push(date);
                }
            });
            return dateStrings;
        }
    }

    isElligible(date: Date, ruleRecord: Record<string, string>): boolean {
        const parsedDateInfo = this.parseDateInfo(date);

        switch (ruleRecord['FREQ']) {
            case 'DAILY':
                return true;

            case 'WEEKLY':
                const days = ruleRecord['BYDAY'].split(',') as DayCode[];
                return days.includes(parsedDateInfo[1]);

            case 'MONTHLY':
                if (ruleRecord['BYMONTHDAY'] && ruleRecord['BYMONTH']) {
                    const days = ruleRecord['BYMONTHDAY'].split(',').map(Number);
                    const months = ruleRecord['BYMONTH'].split(',').map(Number);
                    return days.includes(Number(parsedDateInfo[0])) && months.includes(Number(parsedDateInfo[3]));
                } else if (ruleRecord['BYDAY']) {
                    const byDayValues = ruleRecord['BYDAY'].split(',');
                    return byDayValues.some(item => {
                        const match = item.match(/^(-?\d)?([A-Z]{2})$/);
                        if (match) {
                            const [, weekNum, dayCode] = match;
                            if (weekNum) {
                                const ordinal = weekNum;
                                return ordinal === parsedDateInfo[2] && dayCode === parsedDateInfo[1];
                            }
                        }
                        return false;
                    });
                } else {
                    return false;
                }

            case 'YEARLY':
                const day = ruleRecord['BYMONTHDAY'];
                const month = ruleRecord['BYMONTH'];
                return month === parsedDateInfo[3] && day === parsedDateInfo[0];
            default:
                log.warn('FeaturesService.isElligible', `Unknown FREQ: ${ruleRecord['FREQ']}`, { freq: ruleRecord['FREQ'] });
                return false;
        }
    };

    ruleToRuleRecord(rrule: string): Record<string, string> {
        if (!rrule) return {};
        const ruleParts = rrule.replace('RRULE:', '').split(';');
        const ruleRecord: Record<string, string> = {};

        ruleParts.forEach(part => {
            const [key, value] = part.split('=');
            ruleRecord[key] = value;
        });
        return ruleRecord;
    };

    parseDateInfo(date: Date): [string, DayCode, string, string, boolean] {

        const day = String(date.getDate());
        const weekday = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'][date.getDay()];

        const dayOfMonth = date.getDate();
        const nthWeek = Math.floor((dayOfMonth - 1) / 7) + 1;

        const isLastDay = dayOfMonth === new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

        const month = String(date.getMonth() + 1);

        return [day, weekday as DayCode, String(nthWeek), month, isLastDay];
    };

    getDateRange(startDateStr: string, endDateStr: string): string[] {
        // Parse as local dates (not UTC) to avoid timezone shift
        const start = parseDateString(startDateStr) ?? new Date(startDateStr);
        const end = parseDateString(endDateStr) ?? new Date(endDateStr);

        const result: string[] = [];

        // Determine the loop direction (forward or backward)
        const step = start <= end ? 1 : -1;

        for (let d = new Date(start); (step > 0 ? d <= end : d >= end); d.setDate(d.getDate() + step)) {
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            result.push(`${year}-${month}-${day}`);
        }

        // If traversing backward, reverse so earliest date is first
        return step === -1 ? result.reverse() : result;
    };

    getRemainingDays(repeat: string | null, startDateStr: string, endDateStr: string | null, currentDateStr: string): number | null {

        const ruleRecord = this.ruleToRuleRecord(repeat || '');
        const isOccurrenceBasedRule = ruleRecord['COUNT'] ? true : false;
        if (!isOccurrenceBasedRule && !endDateStr) return null;
        const eligibleDates = this.getAllDatesOfEntity(ruleRecord, startDateStr, endDateStr, startDateStr, endDateStr ?? '9999-12-31');
        if (!eligibleDates || eligibleDates.length === 0) return null;

        // Filter to only dates >= current date (remaining occurrences)
        const remainingDates = eligibleDates.filter(d => d >= currentDateStr);

        // If no remaining dates, the repeat has already ended
        if (remainingDates.length === 0) return null;

        // Only show badge when there are 1-4 remaining occurrences
        // Return count: 1 = last occurrence (today), 2 = 2 occurrences left, 3 = 3 left, 4 = 4 left
        // Note: UI displays as "days" per QA requirement, but value represents occurrences
        if (remainingDates.length <= 4) {
            return remainingDates.length;
        }

        return null;
    }
}
